'use client';

import { useState } from 'react';
import Link from 'next/link';
import { facultiesData } from '@/data/faculties';
import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faTimes, faChevronUp, faChevronDown, faExternalLinkAlt, faHome
} from '@fortawesome/free-solid-svg-icons';

export default function FakultasContent() {
  const { t, language } = useTranslation();
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
  const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';
  const currentSiteName = language === 'en' ? siteNameEnglish : siteName;
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredFaculties = facultiesData.filter(faculty => {
    const name = language === 'en' && faculty.nameEn ? faculty.nameEn : faculty.name;
    const description = language === 'en' && faculty.descriptionEn ? faculty.descriptionEn : faculty.description;
    const programs = language === 'en' && faculty.programsEn ? faculty.programsEn : faculty.programs;

    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      programs?.some(program => program.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <>
      {/* Hero Section with Animated Background */}
      <section className="pt-36 pb-24 bg-gradient-to-br from-unmulDarkGreen via-unmulGreen to-teal-700 text-white relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-unmulYellow rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: 'white', stopOpacity: 0.1 }} />
                <stop offset="100%" style={{ stopColor: 'white', stopOpacity: 0 }} />
              </linearGradient>
            </defs>
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="url(#grad)" />
          </svg>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Breadcrumbs */}
          <nav className="text-sm text-green-200 mb-8 flex items-center gap-2">
            <Link href="/" className="hover:text-white transition flex items-center gap-1">
              <FontAwesomeIcon icon={faHome} className="w-4 h-4" />
              {t('nav.home')}
            </Link>
            <span>/</span>
            <span className="text-unmulYellow font-medium">{t('faculties.breadcrumb')}</span>
          </nav>

          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-unmulYellow font-bold">{t('faculties.badge.count').replace('{count}', facultiesData.length.toString())}</span>
              <span className="mx-2 text-white/50">•</span>
              <span className="text-white">{t('faculties.badge.programs')}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold mb-6 leading-tight">
              {t('faculties.title.explore')} <span className="text-unmulYellow">{t('faculties.title.faculties')}</span>
              <br />
              <span className="text-4xl md:text-5xl lg:text-6xl">{t('faculties.title.programs')}</span>
            </h1>

            <p className="text-xl text-green-100 mb-6 max-w-3xl mx-auto">
              {t('faculties.subtitle', { siteName: currentSiteName })}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section with Icons */}
      <section className="py-16 bg-white border-b">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center p-6 rounded-xl bg-gradient-to-br from-unmulGreen to-teal-600 text-white transform hover:scale-105 transition">
              <div className="text-5xl font-bold mb-2">15</div>
              <div className="text-sm uppercase tracking-wider opacity-80">{t('faculties.stats.faculties')}</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-white border border-gray-100 shadow-lg transform hover:scale-105 transition">
              <div className="text-5xl font-bold text-unmulYellow mb-2">101</div>
              <div className="text-sm uppercase tracking-wider text-gray-500">{t('faculties.stats.program')}</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-white border border-gray-100 shadow-lg transform hover:scale-105 transition">
              <div className="text-5xl font-bold text-unmulGreen mb-2">4</div>
              <div className="text-sm uppercase tracking-wider text-gray-500">{t('faculties.stats.level')}</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-white border border-gray-100 shadow-lg transform hover:scale-105 transition">
              <div className="text-5xl font-bold text-gray-800 mb-2">2</div>
              <div className="text-sm uppercase tracking-wider text-gray-500">{t('faculties.stats.profession')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Faculty Grid with Creative Cards */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white min-h-screen">
        <div className="container mx-auto px-6">
          {/* Section Header & Search */}
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl font-serif font-bold text-gray-800">
              {t('faculties.section.title')} <span className="text-unmulGreen underline decoration-unmulYellow decoration-4 underline-offset-4">{t('faculties.section.titleHighlight')}</span>
            </h2>
            <p className="text-lg text-gray-600">{t('faculties.section.subtitle')}</p>

            {/* Search Bar - Centered */}
            <div className="max-w-lg mx-auto mt-8 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('faculties.search')}
                className="w-full pl-12 pr-4 py-4 rounded-full border-2 border-transparent focus:border-unmulGreen focus:ring-4 focus:ring-unmulGreen/10 bg-white shadow-lg text-lg transition outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-4 text-gray-400 hover:text-red-500 transition"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>

          {/* Faculty Cards - Staggered Grid */}
          {filteredFaculties.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredFaculties.map((faculty, index) => {
                const currentName = language === 'en' && faculty.nameEn ? faculty.nameEn : faculty.name;
                const currentDescription = language === 'en' && faculty.descriptionEn ? faculty.descriptionEn : faculty.description;
                const currentPrograms = language === 'en' && faculty.programsEn ? faculty.programsEn : faculty.programs;

                return (
                  <div
                    key={faculty.id}
                    className="group relative"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 border-transparent hover:border-unmulYellow transform hover:-translate-y-2">
                      {/* Image with Gradient Overlay */}
                      <div className="relative h-56 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10"></div>
                        <img
                          src={faculty.image}
                          alt={currentName}
                          className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://via.placeholder.com/400x300/004d26/FFCC00?text=${encodeURIComponent(currentName)}`;
                          }}
                        />
                        {/* Floating Badge */}
                        <div className="absolute top-4 right-4 z-20 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold text-white border border-white/30">
                          {t('faculties.card.programs').replace('{count}', currentPrograms?.length.toString() || '0')}
                        </div>
                        {/* Faculty Name Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                          <h3 className="text-xl font-bold text-white drop-shadow-lg line-clamp-2">
                            {currentName}
                          </h3>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-6 flex flex-col flex-grow">
                        <p className="text-gray-600 mb-6 text-sm line-clamp-2 min-h-[40px] group-hover:text-gray-900 transition-colors">
                          {currentDescription}
                        </p>

                        {/* Programs List (Collapsible) */}
                        <div className="space-y-3 mb-6 flex-grow">
                          <div className={`transition-all duration-300 overflow-hidden ${expandedId === faculty.id ? 'max-h-96' : 'max-h-24 relative'}`}>
                            <ul className="space-y-2">
                              {currentPrograms?.map((program, index) => (
                                <li key={index} className="flex items-start text-sm text-gray-700">
                                  <span className={`w-1.5 h-1.5 rounded-full ${faculty.color} mt-1.5 mr-2 shrink-0 opacity-70`}></span>
                                  <span className="hover:text-unmulGreen transition-colors cursor-default">{program}</span>
                                </li>
                              ))}
                            </ul>

                            {/* Fade overlay when collapsed */}
                            {expandedId !== faculty.id && (currentPrograms?.length || 0) > 3 && (
                              <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                            )}
                            {(currentPrograms?.length || 0) > 3 && (
                              <button
                                onClick={() => toggleExpand(faculty.id)}
                                className="text-unmulGreen text-xs font-semibold hover:underline focus:outline-none flex items-center gap-1"
                              >
                                {expandedId === faculty.id ? (
                                  <>
                                    {t('faculties.card.hide')} <FontAwesomeIcon icon={faChevronUp} />
                                  </>
                                ) : (
                                  <>
                                    {t('faculties.card.showAll')} <FontAwesomeIcon icon={faChevronDown} />
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Card Footer */}
                        <div className="pt-4 mt-auto border-t border-gray-50">
                          <Link
                            href={faculty.slug || '#'}
                            target="_blank"
                            className={`flex items-center justify-between w-full px-4 py-3 rounded-xl bg-gray-50 text-gray-700 text-sm font-semibold hover:bg-unmulGreen hover:text-white transition-all group/btn`}
                          >
                            <span>{t('faculties.card.visit')}</span>
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="group-hover/btn:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-dashed border-gray-200">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faSearch} className="text-gray-300 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('faculties.noResults.title')}</h3>
              <p className="text-gray-500 max-w-sm mx-auto mb-6">{t('faculties.noResults.subtitle')}</p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-unmulGreen font-semibold hover:underline"
              >
                {t('faculties.noResults.reset')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-unmulGreen text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">{t('faculties.cta.title')}</h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            {t('faculties.cta.subtitle', { siteName: currentSiteName })}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/pendaftaran"
              className="px-8 py-4 bg-unmulYellow text-unmulDarkGreen rounded-xl font-bold hover:bg-yellow-400 transform hover:-translate-y-1 transition shadow-lg shadow-yellow-900/20"
            >
              {t('faculties.cta.button')}
            </Link>
            <Link
              href="/kontak"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-bold hover:bg-white/20 transform hover:-translate-y-1 transition"
            >
              {t('faculties.cta.contact')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
